import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { DeleteModal, NoticeboardButton } from "../../shared";
import { useAuth } from "../../../context/AuthContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import { useParentFamilyData } from "../../../hooks/useParentFamilyData";
import { useUsers } from "../../../hooks/useUsers";
import { api } from "../../../services/api";
import { uploadFileToPresignedUrl } from "../../../utils/presignedUpload";
import { buildFeeStatementPdfBlob } from "../../../utils/feeStatementPdfExport";
import {
  getChildGuardianUserIds,
  publishDirectNoticesToUsers,
} from "../../../utils/feeStatementSend";
import type {
  ChildDetailTab,
  ChildExtracurricular,
  ChildFeeAccount,
  ParentChild,
  ParentFamilyMainTab,
  ReportTermKey,
} from "../../../types/parentFamily";
import {
  calculateAgeFromDob,
  formatChildGradeLabel,
  getChildFullName,
  getChildInitials,
} from "../../../utils/parentFamilyHelpers";
import { todayIsoDate } from "../../../utils/familyDateUtils";
import {
  buildSchoolFeeNotice,
} from "../../../utils/schoolFeesHelpers";
import {
  buildFeeAccountNotice,
} from "../../../utils/feeAccountHelpers";
import {
  REPORT_TERM_KEYS,
  computeLearnerAverage,
} from "../../../utils/reportCardCaps";
import { getAvatarColor } from "../../../utils/avatarUtils";
import { lightTheme } from "../../../styles/theme";
import {
  FAMILY_SECTION_PAPER_SX,
  FAMILY_SIDEBAR_ITEM_SX,
  FAMILY_TAB_SX,
} from "./familyStyles";
import { ChildFormModal, ChildFormValues } from "./ChildFormModal";
import SchoolFeeSetupModal, {
  SchoolFeeSetupValues,
} from "./SchoolFeeSetupModal";
import PaymentRecordModal, { PaymentRecordValues } from "./PaymentRecordModal";
import FeeStatementViewModal from "./FeeStatementViewModal";
import FeeAccountFormModal, {
  FeeAccountFormValues,
} from "./FeeAccountFormModal";
import FeeAccountStatementModal from "./FeeAccountStatementModal";
import {
  ExtracurricularFormModal,
  ExtracurricularFormValues,
} from "./ExtracurricularFormModal";
import {
  ReportCardFormModal,
  ReportCardFormValues,
} from "./ReportCardFormModal";
import { ReportCardViewModal } from "./ReportCardViewModal";
import { LinkGuardianModal } from "./LinkGuardianModal";
import { useFamilyModals } from "./useFamilyModals";
import {
  ChildOverviewSection,
  renderLinkedCirclesPanel,
} from "./ChildOverviewSection";
import { FeeStatementsSection } from "./FeeStatementsSection";
import { ExtracurricularSection } from "./ExtracurricularSection";
import { ReportCardsSection } from "./ReportCardsSection";
import { ChildCertificatesSection } from "./ChildCertificatesSection";

interface UserCircleOption {
  circleId: string;
  name: string;
  alias?: string;
}

interface ManageUserFamilyProps {
  orgId: string;
  userId: string;
  userName?: string;
  userCircles: UserCircleOption[];
  circlesPanel: React.ReactNode;
  /** When false, only the circles panel is shown (same as pre-family user home). */
  familyEnabled?: boolean;
}

export const ManageUserFamily: React.FC<ManageUserFamilyProps> = ({
  orgId,
  userId,
  userName,
  userCircles,
  circlesPanel,
  familyEnabled = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user: authUser, token } = useAuth();
  const { showMessage } = useSnackbar();
  const organizationName = authUser?.schoolName?.trim() || undefined;
  const organizationLogoUrl =
    authUser?.schoolLogo && authUser.schoolLogo !== "/placeholder.jpg"
      ? authUser.schoolLogo
      : undefined;

  const {
    children,
    isHydrated,
    isSaving,
    error,
    setError,
    stats,
    upsertChild,
    deleteChild,
    updateChildCircles,
    upsertSchoolFees,
    recordPayment,
    deletePayment,
    deleteSchoolFees,
    upsertFeeAccount,
    deleteFeeAccount,
    cancelFeeAccount,
    recordFeeAccountPayment,
    deleteFeeAccountPayment,
    markFeeAccountStatementSent,
    setFeeAccountPaidInFull,
    markStatementSent,
    setFeesPaidInFull,
    upsertExtracurricular,
    addExtracurriculars,
    deleteExtracurricular,
    upsertReportCard,
    deleteReportCard,
    addGuardian,
    removeGuardian,
  } = useParentFamilyData(orgId, userId, familyEnabled);

  const { data: orgUsers = [] } = useUsers();
  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of orgUsers) {
      map.set(u.id, u.name);
    }
    return map;
  }, [orgUsers]);

  const [mainTab, setMainTab] = useState<ParentFamilyMainTab>("children");
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [childDetailTab, setChildDetailTab] = useState<ChildDetailTab>("overview");

  const modals = useFamilyModals();
  const {
    childModalOpen,
    setChildModalOpen,
    editingChild,
    openChildModal,
    feeSetupOpen,
    setFeeSetupOpen,
    feeSetupChildId,
    setFeeSetupChildId,
    paymentModalOpen,
    setPaymentModalOpen,
    editingPayment,
    paymentModalChildId,
    openRecordPayment,
    feeAccountPaymentOpen,
    setFeeAccountPaymentOpen,
    feeAccountPaymentChildId,
    feeAccountPaymentAccountId,
    editingFeeAccountPayment,
    openFeeAccountPayment,
    viewLedgerChildId,
    setViewLedgerChildId,
    feeAccountModalOpen,
    setFeeAccountModalOpen,
    feeAccountChildId,
    editingFeeAccount,
    openAddFeeAccount,
    openEditFeeAccount,
    viewFeeAccountId,
    setViewFeeAccountId,
    viewFeeAccountChildId,
    setViewFeeAccountChildId,
    openViewFeeAccount,
    activityModalOpen,
    setActivityModalOpen,
    editingActivity,
    activityModalChildId,
    setActivityModalChildId,
    openAddActivity: openAddActivityModal,
    openEditActivity,
    reportModalOpen,
    setReportModalOpen,
    editingReport,
    reportModalChildId,
    setReportModalChildId,
    openAddReport: openAddReportModal,
    openEditReport,
    viewingReport,
    setViewingReport,
    viewReportChildId,
    setViewReportChildId,
    openViewReport,
    linkGuardianOpen,
    setLinkGuardianOpen,
    deleteTarget,
    setDeleteTarget,
  } = modals;

  const [detailMenuAnchor, setDetailMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [sendSuccessMessage, setSendSuccessMessage] = useState<string | null>(null);
  const [sendingFeeId, setSendingFeeId] = useState<string | null>(null);
  const [sendingFeeAccountId, setSendingFeeAccountId] = useState<string | null>(
    null
  );
  const [expandedPaymentSections, setExpandedPaymentSections] = useState<Set<string>>(
    () => new Set()
  );

  const selectedChild = useMemo(
    () => children.find((c) => c.id === selectedChildId) || null,
    [children, selectedChildId]
  );

  useEffect(() => {
    if (children.length === 0) {
      setSelectedChildId(null);
      return;
    }
    if (!selectedChildId || !children.some((c) => c.id === selectedChildId)) {
      setSelectedChildId(children[0].id);
    }
  }, [children, selectedChildId]);

  // The modal closes straight away; the toast says when the save lands. A
  // failure shows in the error banner, set by the family data hook.
  const handleSaveChild = async (values: ChildFormValues) => {
    try {
      await upsertChild({
        id: values.id,
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: values.dateOfBirth || undefined,
        gender: values.gender || undefined,
        grade: values.grade || undefined,
        classSection: values.classSection || undefined,
        studentId: values.studentId || undefined,
        notes: values.notes || undefined,
        status: values.status,
      });
    } catch {
      return;
    }
    showMessage({
      message: `${getChildFullName(values)} ${values.id ? "saved" : "added"}`,
      severity: "success",
    });
  };

  const contactPhone = authUser?.phoneNumber?.trim() || undefined;
  const contactEmail = authUser?.email?.trim() || undefined;
  const contactWebsite = authUser?.website?.trim() || undefined;

  const formatSendSuccessMessage = (recipientIds: string[]) => {
    const names = recipientIds.map((id) => userNameById.get(id) || "parent");
    if (names.length === 1) {
      return `Statement sent to ${names[0]}.`;
    }
    return `Statement sent to ${names.join(", ")}.`;
  };

  const handleSaveFeeSetup = async (values: SchoolFeeSetupValues) => {
    const childId = values.childId || feeSetupChildId || selectedChildId;
    if (!childId) {
      throw new Error("Select which child these fees are for.");
    }
    await upsertSchoolFees(childId, {
      academicYear: values.academicYear.trim(),
      totalAnnualFee: Number.parseFloat(values.totalAnnualFee),
      paymentPlanMonths: Number.parseInt(values.paymentPlanMonths, 10),
      dayType: values.dayType || undefined,
    });
  };

  const handleSavePayment = async (values: PaymentRecordValues) => {
    const childId = paymentModalChildId || selectedChildId;
    if (!childId) {
      throw new Error("Select a child before recording a payment.");
    }
    await recordPayment(childId, {
      id: values.id,
      paymentNumber: values.paymentNumber,
      paidDate: values.paidDate,
      amountPaid: Number.parseFloat(values.amountPaid),
      receiptNo: values.receiptNo.trim(),
    });
  };

  const handleSendFeeStatement = async (child: ParentChild) => {
    if (sendingFeeId || !child.schoolFees) return;
    const organizationId = authUser?.schoolId || authUser?.orgId || orgId;
    if (!organizationId || !token) {
      setError("Please sign in again to send the fee statement.");
      return;
    }

    const recipients = getChildGuardianUserIds(child, userId);

    setSendingFeeId(child.id);
    setError(null);
    setSendSuccessMessage(null);
    try {
      const notice = buildSchoolFeeNotice(child, child.schoolFees);

      let blob: Blob;
      let fileName: string;
      try {
        ({ blob, fileName } = await buildFeeStatementPdfBlob(
          child,
          child.schoolFees,
          {
            organizationName,
            organizationLogoUrl,
            contact: {
              phone: contactPhone,
              email: contactEmail,
              website: contactWebsite,
            },
          }
        ));
      } catch (pdfErr) {
        console.error("Fee statement PDF generation failed:", pdfErr);
        setError("Could not generate the fee statement PDF. Please try again.");
        return;
      }

      const contentType = "application/pdf";
      let fileUrl: string;
      try {
        const presigned = await api.notices.getPresignedUrl(
          fileName,
          contentType,
          organizationId,
          undefined,
          token
        );
        await uploadFileToPresignedUrl({
          presignedUrl: presigned.presignedUrl,
          file: blob,
          contentType,
        });
        fileUrl = presigned.fileUrl;
      } catch (uploadErr) {
        console.error("Fee statement PDF upload failed:", uploadErr);
        setError(
          "Could not upload the fee statement PDF. Check your connection and try again."
        );
        return;
      }

      await publishDirectNoticesToUsers(
        recipients,
        {
          title: notice.title,
          body: notice.body,
          uploadedFiles: [
            {
              fileUrl,
              fileName,
              contentType,
              size: blob.size,
            },
          ],
        },
        token
      );

      await markStatementSent(child.id);
      setSendSuccessMessage(formatSendSuccessMessage(recipients));
    } catch (err) {
      console.error("Failed to send fee statement:", err);
      setError(
        err instanceof Error ? err.message : "Could not send the fee statement."
      );
    } finally {
      setSendingFeeId(null);
    }
  };

  const handleSendFeeAccountStatement = async (
    child: ParentChild,
    account: ChildFeeAccount
  ) => {
    if (sendingFeeAccountId || !token) return;
    const recipients = getChildGuardianUserIds(child, userId);

    setSendingFeeAccountId(account.id);
    setError(null);
    setSendSuccessMessage(null);
    try {
      const notice = buildFeeAccountNotice(child, account);
      await publishDirectNoticesToUsers(
        recipients,
        {
          title: notice.title,
          body: notice.body,
        },
        token
      );
      await markFeeAccountStatementSent(child.id, account.id);
      setSendSuccessMessage(formatSendSuccessMessage(recipients));
    } catch (err) {
      console.error("Failed to send fee account statement:", err);
      setError(
        err instanceof Error ? err.message : "Could not send the statement."
      );
    } finally {
      setSendingFeeAccountId(null);
    }
  };

  const handleSaveFeeAccount = async (values: FeeAccountFormValues) => {
    const childId = feeAccountChildId;
    if (!childId) {
      throw new Error("Select a child before adding a charge.");
    }
    const totalAmount =
      values.billingPattern === "once-off"
        ? Number.parseFloat(values.totalAmount.replace(/,/g, ""))
        : undefined;
    const monthlyAmount =
      values.billingPattern === "recurring-fixed"
        ? Number.parseFloat(values.monthlyAmount.replace(/,/g, ""))
        : undefined;
    await upsertFeeAccount(childId, {
      id: values.id,
      title: values.title,
      category: values.category,
      billingPattern: values.billingPattern,
      startDate: values.startDate,
      endDate: values.endDate || undefined,
      totalAmount:
        totalAmount != null && Number.isFinite(totalAmount)
          ? totalAmount
          : undefined,
      dueDate: values.dueDate || undefined,
      monthlyAmount:
        monthlyAmount != null && Number.isFinite(monthlyAmount)
          ? monthlyAmount
          : undefined,
      linkedActivityId: values.linkedActivityId,
    });
  };

  const handleSaveFeeAccountPayment = async (values: PaymentRecordValues) => {
    const childId = feeAccountPaymentChildId;
    const accountId = feeAccountPaymentAccountId;
    if (!childId || !accountId) {
      throw new Error("Could not determine which charge this payment is for.");
    }
    await recordFeeAccountPayment(childId, accountId, {
      id: values.id,
      paymentNumber: values.paymentNumber,
      paidDate: values.paidDate,
      amountPaid: Number.parseFloat(values.amountPaid),
      receiptNo: values.receiptNo.trim(),
    });
  };

  const startBillingFromActivity = async (
    child: ParentChild,
    activity: ChildExtracurricular
  ) => {
    const existing = child.feeAccounts.find(
      (a) => a.linkedActivityId === activity.id
    );
    if (existing) {
      openEditFeeAccount(child.id, existing);
      return;
    }
    if (activity.amount == null || activity.amount <= 0) {
      setError("Add a monthly amount to this activity before starting billing.");
      return;
    }
    await upsertFeeAccount(child.id, {
      title: activity.name,
      category: "extracurricular",
      billingPattern: "recurring-fixed",
      startDate: todayIsoDate(),
      monthlyAmount: activity.amount,
      linkedActivityId: activity.id,
    });
  };

  const handleSaveActivity = (valuesList: ExtracurricularFormValues[]) => {
    if (valuesList.length === 0) return;
    const childId = valuesList[0].childId || activityModalChildId;
    if (!childId) return;

    const activities = valuesList.map((values) => {
      const amount = values.amount.trim()
        ? Number.parseFloat(values.amount)
        : undefined;
      return {
        id: values.id,
        name: values.name.trim(),
        schedule: values.schedule || undefined,
        notes: values.notes || undefined,
        amount: amount != null && Number.isFinite(amount) ? amount : undefined,
        currency: values.currency.trim() || "ZAR",
      };
    });

    // Editing targets a single existing activity; adding may allocate many.
    if (activities.length === 1 && activities[0].id) {
      void upsertExtracurricular(childId, activities[0]);
    } else {
      void addExtracurriculars(childId, activities);
    }
  };

  const handleSaveReport = (values: ReportCardFormValues) => {
    const childId = values.childId || reportModalChildId;
    if (!childId) return;
    const subjects = values.subjects.map((s) => ({
      ...s,
      name: s.name.trim(),
    }));
    const learnerAverages: Partial<Record<ReportTermKey, number | null>> = {};
    for (const term of REPORT_TERM_KEYS) {
      learnerAverages[term] = computeLearnerAverage(subjects, term);
    }
    const daysAbsentRaw = values.daysAbsent.trim();
    const daysAbsentParsed =
      daysAbsentRaw === "" ? null : Number(daysAbsentRaw);
    void upsertReportCard(childId, {
      id: values.id,
      periodLabel: values.periodLabel.trim(),
      title: values.title.trim() || undefined,
      phase: values.phase.trim() || undefined,
      academicYear: values.academicYear.trim() || undefined,
      daysAbsent:
        daysAbsentParsed != null && Number.isFinite(daysAbsentParsed)
          ? daysAbsentParsed
          : null,
      subjects,
      learnerAverages,
      teacherComment: values.teacherComment.trim() || undefined,
      teacherSignedAt: values.teacherSignedAt || undefined,
      principalComment: values.principalComment.trim() || undefined,
      principalSignedAt: values.principalSignedAt || undefined,
      schoolClosesOn: values.schoolClosesOn || undefined,
      schoolReopensOn: values.schoolReopensOn || undefined,
      overallResult: values.overallResult.trim() || undefined,
      issuedDate: values.issuedDate || undefined,
      notes: values.notes.trim() || values.teacherComment.trim() || undefined,
    });
  };

  const openFeeSetup = (childId?: string) => {
    if (children.length === 0) {
      openChildModal(null);
      return;
    }
    const resolvedChildId =
      childId ||
      selectedChildId ||
      (children.length === 1 ? children[0].id : null);
    modals.openFeeSetup(resolvedChildId);
  };

  const openAddActivity = (childId?: string) => {
    if (children.length === 0) {
      return;
    }
    const resolvedChildId =
      childId || (children.length === 1 ? children[0].id : null);
    openAddActivityModal(resolvedChildId);
  };

  const openAddReport = (childId?: string) => {
    if (children.length === 0) {
      openChildModal(null);
      return;
    }
    const resolvedChildId =
      childId || (children.length === 1 ? children[0].id : null);
    openAddReportModal(resolvedChildId);
  };

  const togglePaymentSection = (sectionKey: string) => {
    setExpandedPaymentSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  useEffect(() => {
    setExpandedPaymentSections(new Set());
  }, [selectedChildId]);

  const renderChildSidebar = () => (
    <Box sx={{ minWidth: 0 }}>
      <Stack spacing={1.25} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1">Family members</Typography>
          {userName ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Linked to {userName}
            </Typography>
          ) : null}
        </Box>
        <NoticeboardButton
          variant="secondary"
          size="medium"
          startIcon={<AddIcon />}
          onClick={() => openChildModal(null)}
          sx={{ alignSelf: "stretch", minHeight: 40, px: 2.5 }}
        >
          Add Family Member
        </NoticeboardButton>
      </Stack>
      <Stack spacing={1}>
        {children.map((child) => {
          const selected = child.id === selectedChildId;
          const age = calculateAgeFromDob(child.dateOfBirth);
          return (
            <Box
              key={child.id}
              sx={FAMILY_SIDEBAR_ITEM_SX(selected)}
              onClick={() => setSelectedChildId(child.id)}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: getAvatarColor(getChildFullName(child), lightTheme),
                    typography: "body2",
                  }}
                >
                  {getChildInitials(child)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {getChildFullName(child)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[formatChildGradeLabel(child), age != null ? `${age} yrs` : null]
                      .filter(Boolean)
                      .join(" · ") || "Details not set"}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );

  const renderChildDetail = () => {
    if (!selectedChild) return null;
    const age = calculateAgeFromDob(selectedChild.dateOfBirth);

    return (
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Paper
          variant="outlined"
          sx={{ p: 2, borderRadius: 2.5, mb: 2, borderColor: "divider" }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.75} alignItems="center">
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: getAvatarColor(getChildFullName(selectedChild), lightTheme),
                  typography: "subtitle1",
                }}
              >
                {getChildInitials(selectedChild)}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {getChildFullName(selectedChild)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {[
                    formatChildGradeLabel(selectedChild),
                    age != null ? `${age} yrs` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <NoticeboardButton
                variant="secondary"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => openChildModal(selectedChild)}
              >
                Edit Family Member
              </NoticeboardButton>
              <IconButton
                size="small"
                aria-label="More actions"
                onClick={(e) => setDetailMenuAnchor(e.currentTarget)}
              >
                <MoreHorizIcon />
              </IconButton>
              <Menu
                anchorEl={detailMenuAnchor}
                open={Boolean(detailMenuAnchor)}
                onClose={() => setDetailMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    setDetailMenuAnchor(null);
                    setLinkGuardianOpen(true);
                  }}
                >
                  <PersonAddAltOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                  Link parent
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setDetailMenuAnchor(null);
                    setDeleteTarget({
                      type: "member",
                      childId: selectedChild.id,
                      id: selectedChild.id,
                      label: getChildFullName(selectedChild),
                    });
                  }}
                  sx={{ color: "error.main" }}
                >
                  <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                  Remove
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>
        </Paper>

        <Tabs
          value={childDetailTab}
          onChange={(_, value: ChildDetailTab) => setChildDetailTab(value)}
          sx={{
            ...FAMILY_TAB_SX,
            mb: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tab label="Overview" value="overview" />
          <Tab label="Fee statements" value="fee-statements" />
          <Tab
            label={`Extracurricular (${selectedChild.extracurriculars.length})`}
            value="extracurricular"
          />
          <Tab
            label={`Report cards (${selectedChild.reportCards?.length || 0})`}
            value="report-cards"
          />
          <Tab label="Certificates" value="certificates" />
        </Tabs>

        {childDetailTab === "overview" && (
          <ChildOverviewSection
            child={selectedChild}
            userId={userId}
            userNameById={userNameById}
            userCircles={userCircles}
            isSaving={isSaving}
            onEditChild={() => openChildModal(selectedChild)}
            onLinkGuardian={() => setLinkGuardianOpen(true)}
            onRemoveGuardian={(guardianUserId) =>
              void removeGuardian(selectedChild.id, guardianUserId)
            }
            onSetChildDetailTab={setChildDetailTab}
            onOpenFeeSetup={openFeeSetup}
            onUpdateChildCircles={(childId, circleIds) =>
              void updateChildCircles(childId, circleIds)
            }
            renderLinkedCircles={(child) =>
              renderLinkedCirclesPanel(child, userCircles, (childId, circleIds) =>
                void updateChildCircles(childId, circleIds)
              )
            }
          />
        )}
        {childDetailTab === "fee-statements" && (
          <FeeStatementsSection
            child={selectedChild}
            userId={userId}
            sendingFeeId={sendingFeeId}
            sendingFeeAccountId={sendingFeeAccountId}
            expandedPaymentSections={expandedPaymentSections}
            onTogglePaymentSection={togglePaymentSection}
            onOpenFeeSetup={openFeeSetup}
            onOpenAddFeeAccount={openAddFeeAccount}
            onViewLedger={setViewLedgerChildId}
            onSendFeeStatement={(child) => void handleSendFeeStatement(child)}
            onOpenRecordPayment={openRecordPayment}
            onSetFeesPaidInFull={(childId, paid) => void setFeesPaidInFull(childId, paid)}
            onEditFeeSetup={(childId) => {
              setFeeSetupChildId(childId);
              setFeeSetupOpen(true);
            }}
            onDeleteSchoolFees={setDeleteTarget}
            onDeletePayment={setDeleteTarget}
            onViewFeeAccount={openViewFeeAccount}
            onSendFeeAccountStatement={(child, account) =>
              void handleSendFeeAccountStatement(child, account)
            }
            onOpenFeeAccountPayment={openFeeAccountPayment}
            onEditFeeAccount={openEditFeeAccount}
            onCancelFeeAccount={(childId, accountId) =>
              void cancelFeeAccount(childId, accountId)
            }
            onSetFeeAccountPaidInFull={(childId, accountId, paid) =>
              void setFeeAccountPaidInFull(childId, accountId, paid)
            }
            onDeleteFeeAccount={setDeleteTarget}
            onDeleteFeeAccountPayment={setDeleteTarget}
          />
        )}
        {childDetailTab === "extracurricular" && (
          <ExtracurricularSection
            child={selectedChild}
            onOpenAddActivity={openAddActivity}
            onEditActivity={openEditActivity}
            onDeleteActivity={setDeleteTarget}
            onStartBillingFromActivity={(child, activity) =>
              void startBillingFromActivity(child, activity)
            }
          />
        )}
        {childDetailTab === "report-cards" && (
          <ReportCardsSection
            child={selectedChild}
            onOpenAddReport={openAddReport}
            onViewReport={openViewReport}
            onEditReport={openEditReport}
            onDeleteReport={setDeleteTarget}
          />
        )}
        {childDetailTab === "certificates" && (
          <ChildCertificatesSection child={selectedChild} parentUserId={userId} />
        )}
      </Box>
    );
  };

  const renderChildrenTab = () => {
    if (children.length === 0) {
      return (
        <Box sx={{ p: { xs: 2, md: 3 }, textAlign: "center" }}>
          <ChildCareOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            No family linked yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a family member to track grade, fees, activities, and circle links for this parent.
          </Typography>
          <NoticeboardButton
            variant="primary"
            startIcon={<AddIcon />}
            onClick={() => openChildModal(null)}
          >
            Add Family Member
          </NoticeboardButton>
        </Box>
      );
    }

    return (
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>{renderChildSidebar()}</Grid>
          <Grid size={{ xs: 12, md: 8, lg: 9 }}>{renderChildDetail()}</Grid>
        </Grid>
      </Box>
    );
  };

  if (!familyEnabled) {
    return <>{circlesPanel}</>;
  }

  if (!isHydrated) {
    return (
      <Paper elevation={0} variant="outlined" sx={FAMILY_SECTION_PAPER_SX}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={0} variant="outlined" sx={FAMILY_SECTION_PAPER_SX}>
        <Tabs
          value={mainTab}
          onChange={(_, value: ParentFamilyMainTab) => setMainTab(value)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            ...FAMILY_TAB_SX,
            px: { xs: 1, md: 2 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tab label={`Family (${stats.childCount})`} value="children" />
          <Tab label={`Circles (${userCircles.length})`} value="circles" />
        </Tabs>

        {error ? (
          <Alert
            severity="error"
            sx={{ mx: 2, mt: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        ) : null}

        {sendSuccessMessage ? (
          <Alert
            severity="success"
            sx={{ mx: 2, mt: 2 }}
            onClose={() => setSendSuccessMessage(null)}
          >
            {sendSuccessMessage}
          </Alert>
        ) : null}

        {mainTab === "children" && renderChildrenTab()}
        {mainTab === "circles" && <Box sx={{ p: 0 }}>{circlesPanel}</Box>}
      </Paper>

      <ChildFormModal
        open={childModalOpen}
        onClose={() => setChildModalOpen(false)}
        initialChild={editingChild}
        onSave={handleSaveChild}
      />
      <SchoolFeeSetupModal
        open={feeSetupOpen}
        onClose={() => setFeeSetupOpen(false)}
        initialAccount={
          children.find((c) => c.id === feeSetupChildId)?.schoolFees || null
        }
        childrenOptions={children}
        selectedChildId={feeSetupChildId}
        onChildChange={setFeeSetupChildId}
        onSave={handleSaveFeeSetup}
      />
      <PaymentRecordModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        ledger={(() => {
          const account =
            children.find((c) => c.id === paymentModalChildId)?.schoolFees || null;
          return account ? { kind: "school-fees" as const, account } : null;
        })()}
        initialPayment={editingPayment}
        onSave={handleSavePayment}
      />
      <FeeStatementViewModal
        open={Boolean(viewLedgerChildId)}
        onClose={() => setViewLedgerChildId(null)}
        account={
          children.find((c) => c.id === viewLedgerChildId)?.schoolFees || null
        }
        child={
          children.find((c) => c.id === viewLedgerChildId) || selectedChild || null
        }
        organizationName={organizationName}
        organizationLogoUrl={organizationLogoUrl}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        contactWebsite={contactWebsite}
      />
      <FeeAccountFormModal
        open={feeAccountModalOpen}
        onClose={() => setFeeAccountModalOpen(false)}
        initialAccount={editingFeeAccount}
        extracurricularActivities={
          children.find((c) => c.id === feeAccountChildId)?.extracurriculars ?? []
        }
        billedActivityIds={
          (children.find((c) => c.id === feeAccountChildId)?.feeAccounts ?? [])
            .filter((a) => a.id !== editingFeeAccount?.id)
            .map((a) => a.linkedActivityId)
            .filter((id): id is string => Boolean(id))
        }
        onSave={handleSaveFeeAccount}
      />
      <PaymentRecordModal
        open={feeAccountPaymentOpen}
        onClose={() => setFeeAccountPaymentOpen(false)}
        ledger={(() => {
          const account =
            children
              .find((c) => c.id === feeAccountPaymentChildId)
              ?.feeAccounts.find((a) => a.id === feeAccountPaymentAccountId) || null;
          return account ? { kind: "fee-account" as const, account } : null;
        })()}
        initialPayment={editingFeeAccountPayment}
        onSave={handleSaveFeeAccountPayment}
      />
      <FeeAccountStatementModal
        open={Boolean(viewFeeAccountId)}
        onClose={() => {
          setViewFeeAccountId(null);
          setViewFeeAccountChildId(null);
        }}
        account={
          children
            .find((c) => c.id === viewFeeAccountChildId)
            ?.feeAccounts.find((a) => a.id === viewFeeAccountId) || null
        }
        child={
          children.find((c) => c.id === viewFeeAccountChildId) || selectedChild || null
        }
      />
      <ExtracurricularFormModal
        open={activityModalOpen}
        onClose={() => setActivityModalOpen(false)}
        initialActivity={editingActivity}
        childrenOptions={children}
        selectedChildId={activityModalChildId}
        onChildChange={setActivityModalChildId}
        onSave={handleSaveActivity}
      />
      <ReportCardFormModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        initialReport={editingReport}
        childrenOptions={children}
        selectedChildId={reportModalChildId}
        onChildChange={setReportModalChildId}
        onSave={handleSaveReport}
      />
      <ReportCardViewModal
        open={Boolean(viewingReport)}
        onClose={() => {
          setViewingReport(null);
          setViewReportChildId(null);
        }}
        report={viewingReport}
        child={
          children.find((c) => c.id === viewReportChildId) ||
          selectedChild ||
          null
        }
        organizationName={organizationName}
        onEdit={
          viewingReport && viewReportChildId
            ? () => {
                const report = viewingReport;
                const childId = viewReportChildId;
                setViewingReport(null);
                setViewReportChildId(null);
                openEditReport(childId, report);
              }
            : undefined
        }
      />
      <LinkGuardianModal
        open={linkGuardianOpen}
        onClose={() => setLinkGuardianOpen(false)}
        excludeUserIds={[
          userId,
          ...(selectedChild?.guardians || []).map((g) => g.userId),
        ]}
        onSelect={async (user) => {
          if (!selectedChild) return;
          await addGuardian(selectedChild.id, user.id, "co-parent");
        }}
      />
      <DeleteModal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!isSaving) setDeleteTarget(null);
        }}
        title={
          deleteTarget?.type === "payment"
            ? "Remove payment"
            : deleteTarget?.type === "fee-account-payment"
              ? "Remove payment"
              : deleteTarget?.type === "fee-account"
                ? "Remove charge"
            : deleteTarget?.type === "school-fees"
              ? "Remove school fees"
              : deleteTarget?.type === "member"
                ? "Remove family member"
                : deleteTarget?.type === "report"
                  ? "Remove report card"
                  : "Remove activity"
        }
        subtitle="This action cannot be undone."
        maxWidth="sm"
      >
        <Typography variant="body1" sx={{ mb: 3 }}>
          Are you sure you want to remove{" "}
          <Typography component="span" fontWeight={600}>
            &ldquo;{deleteTarget?.label}&rdquo;
          </Typography>
          {deleteTarget?.type === "school-fees"
            ? "? All recorded payments and fee setup for this year will be removed."
            : deleteTarget?.type === "fee-account"
              ? "? All payments for this charge will be removed."
            : deleteTarget?.type === "activity"
            ? "?"
            : deleteTarget?.type === "member"
              ? "? Their fees, activities, report cards, and parent links will also be removed."
              : "?"}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <NoticeboardButton
            variant="secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={isSaving}
          >
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="danger"
            loading={isSaving}
            disabled={isSaving}
            onClick={() => {
              if (!deleteTarget) return;
              const target = deleteTarget;
              void (async () => {
                if (target.type === "payment") {
                  await deletePayment(target.childId, target.id);
                } else if (target.type === "fee-account-payment") {
                  await deleteFeeAccountPayment(
                    target.childId,
                    target.accountId,
                    target.id
                  );
                } else if (target.type === "fee-account") {
                  await deleteFeeAccount(target.childId, target.id);
                } else if (target.type === "school-fees") {
                  await deleteSchoolFees(target.childId);
                } else if (target.type === "member") {
                  await deleteChild(target.childId);
                } else if (target.type === "report") {
                  await deleteReportCard(target.childId, target.id);
                } else {
                  await deleteExtracurricular(target.childId, target.id);
                }
                setDeleteTarget(null);
              })();
            }}
          >
            {isSaving ? "Removing..." : "Remove"}
          </NoticeboardButton>
        </Box>
      </DeleteModal>
    </>
  );
};
